import * as React from "react";
import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";
import { XIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { ButtonShadcn as Button } from "@/components/ui/Button";

const Dialog = (props) => {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
};

// Base UI's outside-click/Escape reasons for a dismissed dialog. Kept as
// plain strings — the REASONS enum they mirror lives under @base-ui/react's
// internal path, not a public export.
const ACCIDENTAL_DISMISS_REASONS = ["outside-press", "escape-key"];

// Wrap a dialog's onClose with this so a stray outside click or Escape can't
// silently drop progress on a multi-step form — only an explicit close
// action (the X button, Back, Cancel) can dismiss it.
const preventAccidentalDismiss = (onClose) => (isOpen, eventDetails) => {
  if (isOpen) return;
  if (ACCIDENTAL_DISMISS_REASONS.includes(eventDetails?.reason)) {
    eventDetails?.cancel?.();
    return;
  }
  onClose();
};

const DialogTrigger = (props) => {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
};

const DialogPortal = (props) => {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
};

const DialogClose = (props) => {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
};

const DialogOverlay = ({ className, ...props }) => {
  return <DialogPrimitive.Backdrop data-slot="dialog-overlay" className={cn("fixed inset-0 z-50 bg-background-primary/90 backdrop-blur-sm", className)} onClick={(e) => e.stopPropagation()} {...props} />;
};

const DialogContent = ({ className, children, showCloseButton = false, ...props }) => {
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Popup
        data-slot="dialog-content"
        className={cn(
          "fixed top-1/2 left-1/2 z-50 w-full max-w-[700px] max-h-[90vh] overflow-hidden -translate-x-1/2 -translate-y-1/2 rounded-xl bg-background-secondary p-0 text-text-primary border border-border-primary shadow-lg",
          className,
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            render={
              <Button variant="ghost" className="absolute top-2 right-2" size="icon-sm" type="button">
                <XIcon />
                <span className="sr-only">Close</span>
              </Button>
            }
          />
        )}
      </DialogPrimitive.Popup>
    </DialogPortal>
  );
};

const DialogHeader = ({ className, ...props }) => {
  return <div data-slot="dialog-header" className={cn("flex flex-col gap-2 p-6 border-b border-border-primary", className)} {...props} />;
};

const DialogFooter = ({ className, showCloseButton = false, children, ...props }) => {
  return (
    <div data-slot="dialog-footer" className={cn("flex justify-end gap-3 p-6 border-t border-border-primary", className)} {...props}>
      {children}

      {showCloseButton && (
        <DialogPrimitive.Close
          render={
            <Button variant="outline" type="button">
              Close
            </Button>
          }
        />
      )}
    </div>
  );
};

const DialogTitle = ({ className, ...props }) => {
  return <DialogPrimitive.Title data-slot="dialog-title" className={cn("text-lg font-semibold", className)} {...props} />;
};

const DialogDescription = ({ className, ...props }) => {
  return <DialogPrimitive.Description data-slot="dialog-description" className={cn("text-sm text-text-secondary", className)} {...props} />;
};

export { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription, DialogClose, preventAccidentalDismiss };
